import { useEffect, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { cls } from "../hatim/soft/SoftKit";
import { useListAuditQuery } from "../../api/adminApi";
import { formatDateTime, roleLabel } from "../../lib/adminFormat";
import {
  PageHeader,
  Section,
  NoData,
  ErrorRetry,
  SkeletonRows,
  Pager,
  Tag,
  SearchInput,
  SelectInput,
  useDebouncedValue,
} from "./components/AdminKit";

const CATEGORY_OPTIONS = [
  { value: "", label: "Tüm kategoriler" },
  { value: "SECURITY", label: "Güvenlik" },
  { value: "MUTATION", label: "İşlem" },
];

function DetailRow({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3 py-1.5">
      <span className={`sm:w-24 shrink-0 text-sm ${cls.faint}`}>{label}</span>
      <span className={`min-w-0 text-sm break-all ${cls.title}`}>{children}</span>
    </div>
  );
}

function EventRow({ ev, open, onToggle }) {
  const security = ev.category === "SECURITY";
  const actor = ev.actor?.email ? `${ev.actor.email}${ev.actor.role ? ` (${roleLabel(ev.actor.role)})` : ""}` : "Anonim";
  const target = ev.target?.summary || (ev.target?.model ? `${ev.target.model} ${ev.target.id || ""}`.trim() : "");
  const hasMeta = ev.metadata && typeof ev.metadata === "object" && Object.keys(ev.metadata).length > 0;

  return (
    <li className={`${cls.row} last:border-b-0`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`w-full text-left px-4 py-3 flex items-start gap-3 ${cls.rowHover}`}
      >
        <span className={`w-1 self-stretch rounded-full shrink-0 ${security ? "bg-terakkiError/60 dark:bg-[#F2A59C]/70" : "bg-terakkiBorder dark:bg-white/20"}`} aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`text-base font-mono break-all ${cls.title}`}>{ev.type}</span>
            <Tag tone={security ? "danger" : "neutral"}>{security ? "Güvenlik" : "İşlem"}</Tag>
          </span>
          <span className={`block text-sm mt-0.5 ${cls.muted}`}>
            {formatDateTime(ev.createdAt)} · {actor}
            {target ? ` · ${target}` : ""}
          </span>
        </span>
        <ExpandMoreIcon
          sx={{ fontSize: 20 }}
          className={`shrink-0 mt-0.5 transition-transform ${cls.faint} ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && (
        <div className="px-4 pb-4 pl-8">
          <div className={`rounded-lg px-3 py-2 bg-terakkiSurfaceAlt dark:bg-white/[0.05]`}>
            <DetailRow label="Zaman">{formatDateTime(ev.createdAt)}</DetailRow>
            <DetailRow label="Kişi">{actor}</DetailRow>
            {ev.target?.model && (
              <DetailRow label="Hedef">
                {ev.target.model}
                {ev.target.id ? ` · ${ev.target.id}` : ""}
                {ev.target.summary ? ` · ${ev.target.summary}` : ""}
              </DetailRow>
            )}
            <DetailRow label="IP">{ev.request?.ip || "Yok"}</DetailRow>
            <DetailRow label="Yol">
              {ev.request?.method || ""} {ev.request?.path || "Yok"}
            </DetailRow>
            {hasMeta && (
              <div className="pt-1.5">
                <span className={`text-sm ${cls.faint}`}>Ayrıntı</span>
                <pre className={`mt-1 text-xs leading-relaxed whitespace-pre-wrap break-all font-mono ${cls.title}`}>
                  {JSON.stringify(ev.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

export default function AuditLogPage() {
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const debouncedQ = useDebouncedValue(q.trim());
  const rangeInvalid = Boolean(from && to && from > to);

  useEffect(() => setPage(1), [category, type, debouncedQ, from, to]);

  const { data, isLoading, isFetching, isError, error, refetch } = useListAuditQuery(
    { category, type, q: debouncedQ, from, to, page },
    { skip: rangeInvalid }
  );
  const events = data?.events || [];
  const typeOptions = [{ value: "", label: "Tüm olaylar" }].concat(
    (data?.types || []).map((x) => ({ value: x, label: x }))
  );
  if (type && !typeOptions.some((o) => o.value === type)) typeOptions.push({ value: type, label: type });

  const dateField =
    "h-10 px-2.5 rounded-lg border border-terakkiBorder bg-terakkiSurface text-base text-terakkiInk outline-none focus:border-terakkiPrimary dark:bg-[#17474A] dark:text-white dark:border-white/[0.14] dark:[color-scheme:dark]";

  let body;
  if (rangeInvalid) body = <NoData text="Başlangıç günü bitiş gününden sonra olamaz" />;
  else if (isLoading) body = <SkeletonRows count={10} />;
  else if (isError) body = <ErrorRetry message={error?.message} onRetry={refetch} error={error} />;
  else if (events.length === 0) body = <NoData text="Süzgeçlere uyan kayıt yok" />;
  else
    body = (
      <ul className={`transition-opacity ${isFetching ? "opacity-60" : ""}`}>
        {events.map((ev) => (
          <EventRow
            key={ev.id}
            ev={ev}
            open={openId === ev.id}
            onToggle={() => setOpenId((cur) => (cur === ev.id ? null : ev.id))}
          />
        ))}
      </ul>
    );

  return (
    <div className="max-w-6xl mx-auto flex flex-col gap-5 pb-8">
      <PageHeader title="Denetim Kayıtları" subtitle="Güvenlik olayları ve durum değiştiren işlemler" />
      <Section bodyClassName="p-0">
        <div className={`px-4 py-3 flex flex-wrap gap-2 items-center ${cls.row}`}>
          <SearchInput value={q} onChange={setQ} placeholder="E-posta ya da hedef ara" className="flex-1 min-w-[200px]" />
          <SelectInput value={category} onChange={setCategory} options={CATEGORY_OPTIONS} label="Kategori" />
          <SelectInput value={type} onChange={setType} options={typeOptions} label="Olay türü" className="max-w-full" />
          <span className="flex items-center gap-1.5">
            <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} aria-label="Başlangıç günü" className={dateField} />
            <span className={cls.faint}>·</span>
            <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} aria-label="Bitiş günü" className={dateField} />
          </span>
          {(category || type || q || from || to) && (
            <button
              type="button"
              onClick={() => {
                setCategory("");
                setType("");
                setQ("");
                setFrom("");
                setTo("");
              }}
              className={`h-10 px-3 text-sm ${cls.accent} hover:underline`}
            >
              Temizle
            </button>
          )}
        </div>
        {body}
        <Pager page={page} hasMore={Boolean(data?.hasMore)} onChange={setPage} busy={isFetching} />
      </Section>
    </div>
  );
}
